import { FC, useState } from 'react';
import { IonButton, IonCardContent, IonContent } from '@ionic/react';
import { Modal } from '../components/Modal';
import { TextField } from '../components/TextField';
import { PhoneField, IPhoneContactMethod } from '../contact-methods/PhoneField';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserState } from './UserState';
import { useSubscription } from '@meraki-internal/state';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { CareMapState } from '../caremap/CareMapState';
import { createUseStyles } from 'react-jss';

export type IAccountSettingsModalProps = {
    isOpen?: boolean;
    closeHandler: () => void;
    required?: boolean;
    requireLastName?: boolean;
};

const useStyles = createUseStyles({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        paddingTop: 8
    },
    readOnlyField: {
        opacity: 0.6,
        pointerEvents: 'none'
    }
});

export const AccountSettingsModal: FC<IAccountSettingsModalProps> = ({
    isOpen = false,
    closeHandler,
    required = false,
    requireLastName = false
}) => {
    const classes = useStyles();

    const userState = useInstance(UserState);
    useSubscription(() => userState);

    const careMapState = useInstance(CareMapState);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [address, setAddress] = useState('');
    const [mobilePhone, setMobilePhone] = useState<IPhoneContactMethod>({ type: 'mobile-phone', value: '' });
    const [workPhone, setWorkPhone] = useState<IPhoneContactMethod>({ type: 'work-phone', value: '' });
    const loadSettings = () => {
        setFirstName(userState.state.firstName || '');
        setLastName(userState.state.lastName || '');
        setAddress(userState.state.address || '');
        setMobilePhone({ type: 'mobile-phone', value: userState.state.phone || '' });
        setWorkPhone({ type: 'work-phone', value: '' });
        mixpanel.track('Account Settings Opened');
    };

    const isValid = (() => {
        if (!firstName.trim()) {
            return false;
        }
        if (requireLastName && !lastName.trim()) {
            return false;
        }
        return true;
    })();

    const getChangedFields = (): string[] => {
        const changed: string[] = [];
        if (firstName !== (userState.state.firstName || '')) {
            changed.push('firstName');
        }
        if (lastName !== (userState.state.lastName || '')) {
            changed.push('lastName');
        }
        if (address !== (userState.state.address || '')) {
            changed.push('address');
        }
        if (mobilePhone.value !== (userState.state.phone || '')) {
            changed.push('mobilePhone');
        }
        return changed;
    };

    const onSave = async () => {
        userState.setFirstName(firstName);
        userState.setLastName(lastName);
        userState.setAddress(address);
        if (mobilePhone.value) {
            userState.setPhone(mobilePhone.value.replace(/\D/g, ''));
        }

        const fieldsChanged = getChangedFields();

        await userState.save();

        careMapState.syncUserProfileToHouseholdMember();

        mixpanel.track('Account Settings Saved', () => ({
            'Fields Changed': fieldsChanged
        }));

        closeHandler();
    };

    return (
        <Modal
            isOpen={isOpen}
            desktopMaxWidth={535}
            title="Account Settings"
            data-id="account-settings-modal"
            closeHandler={closeHandler}
            onDidPresent={loadSettings}
            hideCloseButton={required}
            preventBackdropDismiss={required}
        >
            <IonContent data-id="account-settings-form">
                <IonCardContent style={{paddingLeft: 22, paddingRight: 22}}>
                    <div className={classes.form}>
                        <div className={classes.readOnlyField}>
                            <TextField
                                name="email"
                                label="Email"
                                value={userState.state.email || ''}
                                onInput={() => {}}
                            />
                        </div>

                        <TextField
                            name="first-name"
                            label="First Name"
                            value={firstName}
                            autocapitalize="words"
                            required
                            onInput={setFirstName}
                        />

                        <TextField
                            name="last-name"
                            label={requireLastName ? 'Last Name' : 'Last Name'}
                            value={lastName}
                            autocapitalize="words"
                            required={requireLastName}
                            onInput={setLastName}
                        />

                        <TextField
                            name="address"
                            label="Address"
                            value={address}
                            autocapitalize="words"
                            onInput={setAddress}
                        />

                        <PhoneField
                            method={mobilePhone}
                            methodChanged={(method) => setMobilePhone(method as IPhoneContactMethod)}
                        />

                        <PhoneField
                            method={workPhone}
                            methodChanged={(method) => setWorkPhone(method as IPhoneContactMethod)}
                        />

                        <IonButton
                            data-id="save-button"
                            expand="block"
                            disabled={!isValid}
                            onClick={onSave}
                        >
                            Save
                        </IonButton>
                    </div>
                </IonCardContent>
            </IonContent>
        </Modal>
    );
};
